import { useEffect, useRef, useState } from "react";
import { animate, motion, useInView } from "framer-motion";
import { EASE, fmtUSD } from "../lib/site";

const Counter = ({ to, format }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2.2,
      ease: EASE,
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);

  return <span ref={ref}>{format(val)}</span>;
};

const MILESTONES = [
  { mark: "$1M", note: "Newark food bank expansion" },
  { mark: "$3M", note: "One millionth meal served" },
  { mark: "$5M", note: "Global education fund launched" },
];

export const Impact = ({ campaign }) => {
  const raised = campaign?.raised ?? 0;
  const pct = campaign?.percent ?? 0;

  const stats = [
    { label: "Raised this campaign", value: raised, format: fmtUSD },
    { label: "Meals served", value: 5000, format: (v) => Math.round(v).toLocaleString("en-US") },
    { label: "Volunteers", value: 45, format: (v) => Math.round(v).toLocaleString("en-US") },
    { label: "Donors so far", value: campaign?.donors ?? 0, format: (v) => Math.round(v).toLocaleString("en-US") },
  ];

  return (
    <section id="progress" data-testid="impact-section" className="bg-linen/70 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <p className="eyebrow">Campaign progress</p>
            <h2 className="mt-5 font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl">
              Proof, <em className="text-brand">not promises.</em>
            </h2>
          </motion.div>
          <p className="max-w-sm text-sm leading-relaxed text-inksoft">
            Every figure on this page updates live as pledges are recorded.
            Transparency is the foundation of trust.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-line bg-line lg:grid-cols-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
              className="bg-cardx p-7 sm:p-9"
              data-testid={`stat-card-${i}`}
            >
              <p className="font-serif text-3xl font-semibold text-ink sm:text-4xl">
                <Counter to={s.value} format={s.format} />
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-inksoft">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="mt-12"
        >
          <div className="flex items-baseline justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-inksoft">
            <span data-testid="progress-label-raised">{fmtUSD(raised)} raised</span>
            <span>{fmtUSD(campaign?.goal ?? 5000000)} goal</span>
          </div>
          <div className="mt-3 h-3 w-full overflow-hidden rounded-full border border-line bg-cardx">
            <motion.div
              data-testid="progress-campaign-goal"
              className="h-full rounded-full bg-brand"
              initial={{ width: 0 }}
              whileInView={{ width: `${Math.max(pct, 0.5)}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1.8, delay: 0.3, ease: EASE }}
            />
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {MILESTONES.map((m) => (
              <div key={m.mark} className="flex items-center gap-4 border-t border-line pt-4">
                <span className="font-serif text-2xl font-semibold text-brand">{m.mark}</span>
                <span className="text-xs leading-snug text-inksoft">{m.note}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
